/**
 * STORY-034: Agent IA Questionnaire Client
 * Service for AI questionnaire and BMAD method
 */

import { supabase } from '@/lib/supabase'
import type {
  ProjectType,
  QuestionConfig,
  QuestionnaireTemplate,
  AIConversation,
  AIMessage,
  InterventionRequest,
  AISummary,
  BMADResponse,
  ConversationChannel,
  ConversationStatus,
  InterventionReason,
} from '@/types/ai'
import {
  generateConversationalResponse,
  analyzeClientResponses,
  shouldRequestIntervention,
} from './gemini'

// BMAD Questionnaire templates by project type
const QUESTIONNAIRE_TEMPLATES: Record<ProjectType, QuestionConfig[]> = {
  site_vitrine: [
    // Background
    {
      id: 'bg_current_presence',
      category: 'background',
      type: 'single_choice',
      question: 'Avez-vous deja une presence en ligne?',
      required: true,
      options: [
        { value: 'no_site', label: 'Non, aucun site' },
        { value: 'old_site', label: 'Oui, un ancien site a remplacer' },
        { value: 'social_only', label: 'Uniquement reseaux sociaux' },
        { value: 'other', label: 'Autre' },
      ],
    },
    {
      id: 'bg_activity',
      category: 'background',
      type: 'text',
      question: 'Decrivez votre activite en quelques mots',
      placeholder: 'Ex: Cabinet de kinesitherapie specialise en sport',
      required: true,
    },
    {
      id: 'bg_target',
      category: 'background',
      type: 'textarea',
      question: 'Qui sont vos clients cibles?',
      placeholder: 'Decrivez votre clientele ideale...',
      required: true,
    },
    // Motivation
    {
      id: 'mo_why_now',
      category: 'motivation',
      type: 'multiple_choice',
      question: 'Pourquoi souhaitez-vous creer ce site maintenant?',
      required: true,
      options: [
        { value: 'visibility', label: 'Augmenter ma visibilite' },
        { value: 'credibility', label: 'Renforcer ma credibilite' },
        { value: 'competition', label: 'Faire face a la concurrence' },
        { value: 'leads', label: 'Generer des contacts' },
        { value: 'info', label: 'Informer mes clients' },
        { value: 'other', label: 'Autre' },
      ],
    },
    {
      id: 'mo_pain_points',
      category: 'motivation',
      type: 'textarea',
      question: 'Quels problemes rencontrez-vous actuellement sans site?',
      required: false,
    },
    // Aspirations
    {
      id: 'as_goals',
      category: 'aspirations',
      type: 'textarea',
      question: 'Quels sont vos objectifs principaux pour ce site?',
      placeholder: 'Ex: Recevoir 10 demandes de contact par mois',
      required: true,
    },
    {
      id: 'as_features',
      category: 'aspirations',
      type: 'multiple_choice',
      question: 'Quelles fonctionnalites souhaitez-vous?',
      required: true,
      options: [
        { value: 'contact_form', label: 'Formulaire de contact' },
        { value: 'booking', label: 'Prise de rendez-vous' },
        { value: 'gallery', label: 'Galerie photos/portfolio' },
        { value: 'blog', label: 'Blog/actualites' },
        { value: 'testimonials', label: 'Temoignages clients' },
        { value: 'map', label: 'Carte/localisation' },
        { value: 'chat', label: 'Chat en ligne' },
      ],
    },
    {
      id: 'as_inspiration',
      category: 'aspirations',
      type: 'textarea',
      question: 'Avez-vous des sites qui vous inspirent?',
      placeholder: 'Listez les URLs ou decrivez ce qui vous plait',
      required: false,
    },
    // Decision-making
    {
      id: 'dm_budget',
      category: 'decisionMaking',
      type: 'budget',
      question: 'Quel est votre budget approximatif?',
      required: true,
      options: [
        { value: '1000-2000', label: '1 000 - 2 000 EUR' },
        { value: '2000-5000', label: '2 000 - 5 000 EUR' },
        { value: '5000-10000', label: '5 000 - 10 000 EUR' },
        { value: '10000+', label: 'Plus de 10 000 EUR' },
        { value: 'unknown', label: 'Je ne sais pas encore' },
      ],
    },
    {
      id: 'dm_timeline',
      category: 'decisionMaking',
      type: 'single_choice',
      question: 'Quel est votre delai souhaite?',
      required: true,
      options: [
        { value: 'urgent', label: 'Moins d\'1 mois' },
        { value: '1-2months', label: '1 a 2 mois' },
        { value: '2-3months', label: '2 a 3 mois' },
        { value: 'flexible', label: 'Flexible' },
      ],
    },
    {
      id: 'dm_decision_maker',
      category: 'decisionMaking',
      type: 'single_choice',
      question: 'Etes-vous le decideur final?',
      required: true,
      options: [
        { value: 'yes', label: 'Oui' },
        { value: 'partial', label: 'En partie, avec d\'autres personnes' },
        { value: 'no', label: 'Non, je collecte des informations' },
      ],
    },
  ],
  site_ecommerce: [
    // Background
    {
      id: 'bg_current_sales',
      category: 'background',
      type: 'single_choice',
      question: 'Comment vendez-vous actuellement?',
      required: true,
      options: [
        { value: 'physical', label: 'Boutique physique uniquement' },
        { value: 'marketplace', label: 'Marketplaces (Amazon, Etsy...)' },
        { value: 'social', label: 'Reseaux sociaux' },
        { value: 'existing_site', label: 'Site existant a remplacer' },
        { value: 'none', label: 'Pas encore de ventes' },
      ],
    },
    {
      id: 'bg_products',
      category: 'background',
      type: 'text',
      question: 'Combien de produits souhaitez-vous vendre?',
      placeholder: 'Nombre approximatif',
      required: true,
    },
    {
      id: 'bg_product_type',
      category: 'background',
      type: 'textarea',
      question: 'Decrivez vos produits',
      required: true,
    },
    // Motivation
    {
      id: 'mo_goals',
      category: 'motivation',
      type: 'multiple_choice',
      question: 'Quels sont vos objectifs principaux?',
      required: true,
      options: [
        { value: 'increase_sales', label: 'Augmenter les ventes' },
        { value: 'new_market', label: 'Atteindre de nouveaux marches' },
        { value: 'reduce_costs', label: 'Reduire les couts de vente' },
        { value: 'autonomy', label: 'Etre independant des marketplaces' },
        { value: 'brand', label: 'Developper ma marque' },
      ],
    },
    // Aspirations
    {
      id: 'as_features',
      category: 'aspirations',
      type: 'multiple_choice',
      question: 'Quelles fonctionnalites e-commerce souhaitez-vous?',
      required: true,
      options: [
        { value: 'payment', label: 'Paiement en ligne' },
        { value: 'stock', label: 'Gestion des stocks' },
        { value: 'shipping', label: 'Calcul des frais de port' },
        { value: 'promo', label: 'Codes promo' },
        { value: 'reviews', label: 'Avis clients' },
        { value: 'wishlist', label: 'Liste de souhaits' },
        { value: 'b2b', label: 'Espace pro B2B' },
      ],
    },
    {
      id: 'as_revenue_goal',
      category: 'aspirations',
      type: 'text',
      question: 'Quel chiffre d\'affaires visez-vous la premiere annee?',
      required: false,
    },
    // Decision-making
    {
      id: 'dm_budget',
      category: 'decisionMaking',
      type: 'budget',
      question: 'Quel est votre budget?',
      required: true,
      options: [
        { value: '3000-5000', label: '3 000 - 5 000 EUR' },
        { value: '5000-10000', label: '5 000 - 10 000 EUR' },
        { value: '10000-20000', label: '10 000 - 20 000 EUR' },
        { value: '20000+', label: 'Plus de 20 000 EUR' },
      ],
    },
    {
      id: 'dm_timeline',
      category: 'decisionMaking',
      type: 'single_choice',
      question: 'Quand souhaitez-vous lancer?',
      required: true,
      options: [
        { value: 'asap', label: 'Le plus vite possible' },
        { value: '2-3months', label: '2 a 3 mois' },
        { value: '3-6months', label: '3 a 6 mois' },
        { value: 'flexible', label: 'Pas de deadline' },
      ],
    },
  ],
  application_web: [
    {
      id: 'bg_current_process',
      category: 'background',
      type: 'textarea',
      question: 'Decrivez le processus actuel que l\'application doit ameliorer',
      required: true,
    },
    {
      id: 'bg_users',
      category: 'background',
      type: 'text',
      question: 'Combien d\'utilisateurs utiliseront l\'application?',
      required: true,
    },
    {
      id: 'mo_problems',
      category: 'motivation',
      type: 'textarea',
      question: 'Quels problemes souhaitez-vous resoudre?',
      required: true,
    },
    {
      id: 'as_features',
      category: 'aspirations',
      type: 'textarea',
      question: 'Listez les fonctionnalites principales souhaitees',
      required: true,
    },
    {
      id: 'as_integrations',
      category: 'aspirations',
      type: 'textarea',
      question: 'Quelles integrations sont necessaires? (autres logiciels, APIs...)',
      required: false,
    },
    {
      id: 'dm_budget',
      category: 'decisionMaking',
      type: 'budget',
      question: 'Quel est votre budget?',
      required: true,
      options: [
        { value: '10000-20000', label: '10 000 - 20 000 EUR' },
        { value: '20000-50000', label: '20 000 - 50 000 EUR' },
        { value: '50000-100000', label: '50 000 - 100 000 EUR' },
        { value: '100000+', label: 'Plus de 100 000 EUR' },
      ],
    },
    {
      id: 'dm_timeline',
      category: 'decisionMaking',
      type: 'single_choice',
      question: 'Quel est votre delai?',
      required: true,
      options: [
        { value: '3-6months', label: '3 a 6 mois' },
        { value: '6-12months', label: '6 a 12 mois' },
        { value: '12months+', label: 'Plus d\'un an' },
      ],
    },
  ],
  application_mobile: [
    {
      id: 'bg_platform',
      category: 'background',
      type: 'single_choice',
      question: 'Sur quelles plateformes?',
      required: true,
      options: [
        { value: 'ios', label: 'iOS uniquement' },
        { value: 'android', label: 'Android uniquement' },
        { value: 'both', label: 'iOS et Android' },
      ],
    },
    {
      id: 'bg_users',
      category: 'background',
      type: 'text',
      question: 'Combien d\'utilisateurs cibles?',
      required: true,
    },
    {
      id: 'mo_purpose',
      category: 'motivation',
      type: 'textarea',
      question: 'Quel est l\'objectif principal de l\'application?',
      required: true,
    },
    {
      id: 'as_features',
      category: 'aspirations',
      type: 'textarea',
      question: 'Quelles fonctionnalites principales?',
      required: true,
    },
    {
      id: 'dm_budget',
      category: 'decisionMaking',
      type: 'budget',
      question: 'Quel budget?',
      required: true,
      options: [
        { value: '15000-30000', label: '15 000 - 30 000 EUR' },
        { value: '30000-60000', label: '30 000 - 60 000 EUR' },
        { value: '60000+', label: 'Plus de 60 000 EUR' },
      ],
    },
  ],
  refonte: [
    {
      id: 'bg_current_url',
      category: 'background',
      type: 'text',
      question: 'URL de votre site actuel',
      required: true,
    },
    {
      id: 'bg_problems',
      category: 'background',
      type: 'multiple_choice',
      question: 'Quels sont les problemes actuels?',
      required: true,
      options: [
        { value: 'design', label: 'Design depasse' },
        { value: 'mobile', label: 'Pas adapte mobile' },
        { value: 'slow', label: 'Trop lent' },
        { value: 'seo', label: 'Mal reference' },
        { value: 'content', label: 'Contenu obsolete' },
        { value: 'features', label: 'Manque de fonctionnalites' },
      ],
    },
    {
      id: 'mo_goals',
      category: 'motivation',
      type: 'textarea',
      question: 'Qu\'attendez-vous de cette refonte?',
      required: true,
    },
    {
      id: 'as_keep',
      category: 'aspirations',
      type: 'textarea',
      question: 'Qu\'est-ce qui fonctionne bien et doit etre conserve?',
      required: false,
    },
    {
      id: 'dm_budget',
      category: 'decisionMaking',
      type: 'budget',
      question: 'Budget pour la refonte?',
      required: true,
      options: [
        { value: '2000-5000', label: '2 000 - 5 000 EUR' },
        { value: '5000-10000', label: '5 000 - 10 000 EUR' },
        { value: '10000-20000', label: '10 000 - 20 000 EUR' },
        { value: '20000+', label: 'Plus de 20 000 EUR' },
      ],
    },
  ],
  maintenance: [
    {
      id: 'bg_site_info',
      category: 'background',
      type: 'text',
      question: 'URL du site a maintenir',
      required: true,
    },
    {
      id: 'bg_technology',
      category: 'background',
      type: 'text',
      question: 'Quelle technologie/CMS? (WordPress, Shopify, sur-mesure...)',
      required: true,
    },
    {
      id: 'mo_needs',
      category: 'motivation',
      type: 'multiple_choice',
      question: 'De quoi avez-vous besoin?',
      required: true,
      options: [
        { value: 'updates', label: 'Mises a jour regulieres' },
        { value: 'security', label: 'Securite' },
        { value: 'backup', label: 'Sauvegardes' },
        { value: 'content', label: 'Mise a jour de contenu' },
        { value: 'support', label: 'Support technique' },
        { value: 'hosting', label: 'Hebergement' },
      ],
    },
    {
      id: 'dm_budget',
      category: 'decisionMaking',
      type: 'budget',
      question: 'Budget mensuel de maintenance?',
      required: true,
      options: [
        { value: '50-100', label: '50 - 100 EUR/mois' },
        { value: '100-200', label: '100 - 200 EUR/mois' },
        { value: '200-500', label: '200 - 500 EUR/mois' },
        { value: '500+', label: 'Plus de 500 EUR/mois' },
      ],
    },
  ],
  consulting: [
    {
      id: 'bg_context',
      category: 'background',
      type: 'textarea',
      question: 'Decrivez votre situation actuelle',
      required: true,
    },
    {
      id: 'mo_topics',
      category: 'motivation',
      type: 'multiple_choice',
      question: 'Sur quels sujets avez-vous besoin de conseil?',
      required: true,
      options: [
        { value: 'strategy', label: 'Strategie digitale' },
        { value: 'tech', label: 'Choix technologiques' },
        { value: 'seo', label: 'Referencement' },
        { value: 'marketing', label: 'Marketing digital' },
        { value: 'security', label: 'Securite' },
        { value: 'performance', label: 'Performance' },
      ],
    },
    {
      id: 'as_outcome',
      category: 'aspirations',
      type: 'textarea',
      question: 'Quel resultat attendez-vous de cette consultation?',
      required: true,
    },
    {
      id: 'dm_format',
      category: 'decisionMaking',
      type: 'single_choice',
      question: 'Quel format preferez-vous?',
      required: true,
      options: [
        { value: 'call', label: 'Appel telephonique' },
        { value: 'visio', label: 'Visioconference' },
        { value: 'meeting', label: 'Rendez-vous physique' },
        { value: 'audit', label: 'Audit ecrit' },
      ],
    },
  ],
  autre: [
    {
      id: 'bg_description',
      category: 'background',
      type: 'textarea',
      question: 'Decrivez votre projet',
      required: true,
    },
    {
      id: 'mo_goals',
      category: 'motivation',
      type: 'textarea',
      question: 'Quels sont vos objectifs?',
      required: true,
    },
    {
      id: 'dm_budget',
      category: 'decisionMaking',
      type: 'text',
      question: 'Avez-vous un budget en tete?',
      required: false,
    },
    {
      id: 'dm_timeline',
      category: 'decisionMaking',
      type: 'text',
      question: 'Quand souhaitez-vous demarrer?',
      required: false,
    },
  ],
}

/**
 * Get questionnaire template for a project type
 */
export function getQuestionnaireTemplate(projectType: ProjectType): QuestionnaireTemplate {
  return {
    id: `template_${projectType}`,
    projectType,
    name: `Questionnaire ${projectType}`,
    description: `Questions BMAD pour un projet de type ${projectType}`,
    questions: QUESTIONNAIRE_TEMPLATES[projectType],
  }
}

/**
 * Get all available project types
 */
export function getProjectTypes(): Array<{ type: ProjectType; label: string; description: string }> {
  return [
    { type: 'site_vitrine', label: 'Site Vitrine', description: 'Site de presentation pour votre activite' },
    { type: 'site_ecommerce', label: 'Site E-commerce', description: 'Boutique en ligne pour vendre vos produits' },
    { type: 'application_web', label: 'Application Web', description: 'Application metier sur mesure' },
    { type: 'application_mobile', label: 'Application Mobile', description: 'Application iOS et/ou Android' },
    { type: 'refonte', label: 'Refonte de site', description: 'Modernisation de votre site existant' },
    { type: 'maintenance', label: 'Maintenance', description: 'Contrat de maintenance et support' },
    { type: 'consulting', label: 'Consulting', description: 'Conseil et accompagnement digital' },
    { type: 'autre', label: 'Autre projet', description: 'Projet sur mesure' },
  ]
}

// AI Service for conversations and interventions
export const aiService = {
  /**
   * Get all AI conversations
   */
  async getConversations(filters?: {
    status?: ConversationStatus
    channel?: ConversationChannel
    search?: string
  }): Promise<AIConversation[]> {
    let query = supabase
      .from('ai_conversations')
      .select('*')
      .order('updated_at', { ascending: false })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.channel) {
      query = query.eq('channel', filters.channel)
    }

    if (filters?.search) {
      query = query.or(
        `client_name.ilike.%${filters.search}%,client_email.ilike.%${filters.search}%`
      )
    }

    const { data, error } = await query

    if (error) throw error
    return (data as AIConversation[]) || []
  },

  /**
   * Get conversation by ID with messages
   */
  async getConversation(id: string): Promise<AIConversation | null> {
    const { data: conversation, error: convError } = await supabase
      .from('ai_conversations')
      .select('*')
      .eq('id', id)
      .single()

    if (convError) throw convError
    if (!conversation) return null

    const { data: messages, error: msgError } = await supabase
      .from('ai_messages')
      .select('*')
      .eq('conversation_id', id)
      .order('timestamp', { ascending: true })

    if (msgError) throw msgError

    return {
      ...conversation,
      messages: messages || [],
    } as AIConversation
  },

  /**
   * Create a new conversation
   */
  async createConversation(params: {
    clientEmail: string
    clientName?: string
    clientId?: string
    channel: ConversationChannel
    projectType?: ProjectType
  }): Promise<AIConversation> {
    const { data, error } = await supabase
      .from('ai_conversations')
      .insert({
        client_email: params.clientEmail,
        client_name: params.clientName,
        client_id: params.clientId,
        channel: params.channel,
        project_type: params.projectType,
        status: 'active' as ConversationStatus,
        ai_enabled: true,
      })
      .select()
      .single()

    if (error) throw error
    return { ...data, messages: [] } as AIConversation
  },

  /**
   * Add a message to a conversation
   */
  async addMessage(
    conversationId: string,
    message: Omit<AIMessage, 'id' | 'conversationId' | 'timestamp'>
  ): Promise<AIMessage> {
    const { data, error } = await supabase
      .from('ai_messages')
      .insert({
        conversation_id: conversationId,
        role: message.role,
        content: message.content,
        metadata: message.metadata,
      })
      .select()
      .single()

    if (error) throw error

    // Update conversation last_message_at
    await supabase
      .from('ai_conversations')
      .update({ last_message_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', conversationId)

    return data as AIMessage
  },

  /**
   * Process user message and generate AI response
   */
  async processMessage(
    conversationId: string,
    userMessage: string
  ): Promise<{ response: AIMessage; interventionRequested: boolean }> {
    // Get conversation with history
    const conversation = await this.getConversation(conversationId)
    if (!conversation) throw new Error('Conversation not found')

    // Check if AI is enabled
    if (!conversation.aiEnabled) {
      return {
        response: {
          id: '',
          conversationId,
          role: 'system',
          content: 'L\'IA est desactivee pour cette conversation. Un admin va prendre le relai.',
          timestamp: new Date().toISOString(),
        },
        interventionRequested: true,
      }
    }

    // Add user message
    await this.addMessage(conversationId, {
      role: 'user',
      content: userMessage,
    })

    // Check if intervention is needed
    const interventionCheck = await shouldRequestIntervention(userMessage, conversation.messages)

    if (interventionCheck.shouldIntervene) {
      // Create intervention request
      await this.createInterventionRequest({
        conversationId,
        reason: (interventionCheck.reason as InterventionReason) || 'ai_uncertainty',
        context: conversation.messages.slice(-5).map((m) => `${m.role}: ${m.content}`).join('\n'),
        lastUserMessage: userMessage,
        priority: interventionCheck.priority || 'medium',
      })

      // Update conversation status
      await supabase
        .from('ai_conversations')
        .update({ status: 'pending_intervention' })
        .eq('id', conversationId)

      const systemMessage = await this.addMessage(conversationId, {
        role: 'assistant',
        content:
          'Votre question necessite l\'intervention d\'un expert de notre equipe. Un conseiller va vous repondre tres prochainement.',
      })

      return { response: systemMessage, interventionRequested: true }
    }

    // Generate AI response
    const aiResponse = await generateConversationalResponse(
      userMessage,
      conversation.messages,
      {
        projectType: conversation.projectType,
        clientName: conversation.clientName,
      }
    )

    // Add AI response to conversation
    const assistantMessage = await this.addMessage(conversationId, {
      role: 'assistant',
      content: aiResponse.text,
      metadata: {
        tokens: aiResponse.tokens.total,
        model: 'gemini-1.5-flash',
      },
    })

    return { response: assistantMessage, interventionRequested: false }
  },

  /**
   * Toggle AI for a conversation
   */
  async toggleAI(conversationId: string, enabled: boolean): Promise<void> {
    const { error } = await supabase
      .from('ai_conversations')
      .update({ ai_enabled: enabled, updated_at: new Date().toISOString() })
      .eq('id', conversationId)

    if (error) throw error
  },

  /**
   * Generate summary for a conversation
   */
  async generateSummary(conversationId: string): Promise<AISummary> {
    const conversation = await this.getConversation(conversationId)
    if (!conversation) throw new Error('Conversation not found')

    const analysis = await analyzeClientResponses(
      conversation.projectType || 'autre',
      conversation.bmadResponses || {},
      conversation.messages
    )

    const summary: AISummary = {
      id: `summary_${conversationId}`,
      conversationId,
      generatedAt: new Date().toISOString(),
      projectType: conversation.projectType || 'autre',
      ...analysis,
    }

    // Store summary
    await supabase
      .from('ai_conversations')
      .update({ summary, updated_at: new Date().toISOString() })
      .eq('id', conversationId)

    return summary
  },

  /**
   * Get intervention requests (all client requests)
   */
  async getInterventionRequests(filters?: {
    status?: InterventionRequest['status']
    priority?: InterventionRequest['priority']
  }): Promise<InterventionRequest[]> {
    let query = supabase
      .from('client_requests')
      .select('*')
      .is('deleted_at', null)  // Exclude deleted records
      .order('created_at', { ascending: false })

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.priority) {
      query = query.eq('priority', filters.priority)
    }

    const { data, error } = await query

    if (error) throw error

    // Map client_requests fields to InterventionRequest
    return (data || []).map((row) => ({
      id: row.id,
      conversationId: row.conversation_id || '', // Empty string if no conversation
      createdAt: row.created_at,
      reason: row.request_type as InterventionReason,
      context: row.description || '',
      lastUserMessage: row.title,
      priority: row.priority as InterventionRequest['priority'],
      assignedAdminId: row.admin_assigned_id || undefined,
      status: row.status as InterventionRequest['status'],
      resolvedAt: row.completed_at || undefined,
      adminNotes: row.assigned_admin_notes || undefined,
    }))
  },

  /**
   * Create intervention request
   */
  async createInterventionRequest(
    request: Omit<InterventionRequest, 'id' | 'createdAt' | 'status'>
  ): Promise<InterventionRequest> {
    // Get conversation to link user_id
    const { data: conversation } = await supabase
      .from('ai_conversations')
      .select('client_id')
      .eq('id', request.conversationId)
      .single()

    const { data, error } = await supabase
      .from('client_requests')
      .insert({
        conversation_id: request.conversationId,
        user_id: conversation?.client_id || request.conversationId, // Fallback to conversation_id if no client
        request_type: request.reason,
        title: request.lastUserMessage,
        description: request.context,
        priority: request.priority,
        status: 'pending',
        is_saved_draft: false,
      })
      .select()
      .single()

    if (error) throw error

    // Map back to InterventionRequest
    return {
      id: data.id,
      conversationId: data.conversation_id!,
      createdAt: data.created_at,
      reason: data.request_type as InterventionReason,
      context: data.description || '',
      lastUserMessage: data.title,
      priority: data.priority as InterventionRequest['priority'],
      assignedAdminId: data.admin_assigned_id || undefined,
      status: data.status as InterventionRequest['status'],
      resolvedAt: data.completed_at || undefined,
      adminNotes: data.assigned_admin_notes || undefined,
    }
  },

  /**
   * Update intervention request
   */
  async updateInterventionRequest(
    id: string,
    updates: Partial<Pick<InterventionRequest, 'status' | 'assignedAdminId' | 'adminNotes'>>
  ): Promise<InterventionRequest> {
    const updateData: Record<string, unknown> = {}

    if (updates.status !== undefined) {
      updateData.status = updates.status
      if (updates.status === 'resolved') {
        updateData.completed_at = new Date().toISOString()
      }
    }

    if (updates.assignedAdminId !== undefined) {
      updateData.admin_assigned_id = updates.assignedAdminId
    }

    if (updates.adminNotes !== undefined) {
      updateData.assigned_admin_notes = updates.adminNotes
    }

    const { data, error } = await supabase
      .from('client_requests')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error

    // Map back to InterventionRequest
    return {
      id: data.id,
      conversationId: data.conversation_id!,
      createdAt: data.created_at,
      reason: data.request_type as InterventionReason,
      context: data.description || '',
      lastUserMessage: data.title,
      priority: data.priority as InterventionRequest['priority'],
      assignedAdminId: data.admin_assigned_id || undefined,
      status: data.status as InterventionRequest['status'],
      resolvedAt: data.completed_at || undefined,
      adminNotes: data.assigned_admin_notes || undefined,
    }
  },

  /**
   * Resolve intervention and continue conversation
   */
  async resolveIntervention(
    interventionId: string,
    adminId: string,
    adminResponse: string,
    notes?: string
  ): Promise<void> {
    // Get intervention
    const { data: intervention, error: intError } = await supabase
      .from('client_requests')
      .select('*')
      .eq('id', interventionId)
      .single()

    if (intError) throw intError
    if (!intervention.conversation_id) throw new Error('No conversation linked to this request')

    // Add admin response to conversation
    await this.addMessage(intervention.conversation_id, {
      role: 'assistant',
      content: adminResponse,
      metadata: { adminId, isHumanResponse: true },
    })

    // Update intervention
    await this.updateInterventionRequest(interventionId, {
      status: 'resolved',
      assignedAdminId: adminId,
      adminNotes: notes,
    })

    // Update conversation status
    await supabase
      .from('ai_conversations')
      .update({ status: 'active', updated_at: new Date().toISOString() })
      .eq('id', intervention.conversation_id)
  },
}

export default aiService
